import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAllEdges,
  selectAllPoints,
  selectFilteredEdgeNodes,
} from '../map/slice/mapSelectors';
import { fromLonLat } from 'ol/proj';
import {
  selectFilteredIOCContracts,
  selectIOCVisibility,
} from '../ioc/iocSlice';
import { IOCSegmentModal } from '../ioc/IOCSegmentModal';
import { Edge, Node, PartialRecord } from '@/types';
import { useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';
import { useMapContext, MapEventListener } from './context/MapContext';

const getPathStyle = () => [
  {
    filter: ['==', ['get', 'priority'], 1],
    style: {
      'stroke-width': 12,
      'stroke-color': 'hsl(200,100%,75%)',
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      'stroke-opacity': 0.8,
    },
  },
  {
    filter: ['==', ['get', 'priority'], 0],
    style: {
      'stroke-width': 12,
      'stroke-color': 'hsl(60,100%,75%)',
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
      'stroke-opacity': 0.8,
    },
  },
];

type PathSegment = [Node, Node];

export type MapIOCPathsProps = {
  zIndex: number;
};

export const MapIOCPaths: React.FC<MapIOCPathsProps> = ({ zIndex }) => {
  const { registerClickListener, unregisterClickListener } = useMapContext();
  const nodes = useSelector(selectFilteredEdgeNodes);
  const allEdges = useSelector(selectAllEdges);
  const allPoints = useSelector(selectAllPoints);
  const iocContracts = useSelector(selectFilteredIOCContracts);
  const visibility = useSelector(selectIOCVisibility);

  const [modalKey, setModalKey] = useState<string | null>(null);

  const isIocPage = useMemo(
    () => window.location.pathname.includes('/index-of-customers'),
    [window.location.pathname],
  );

  const clickListener: MapEventListener = useCallback(
    (feature, _) => {
      if (!isIocPage) return;
      if (feature?.getProperties()?.key?.startsWith('ioc-')) {
        const lookup = feature.getProperties()?.id;
        if (feature?.getProperties()?.type === 'edge') {
          setModalKey(lookup);
        }
      }

      if (feature?.getProperties()?.key?.startsWith('edge-')) {
        const edgeId = feature.getProperties()?.id;
        const edge = allEdges[edgeId];
        const lookup = `${edge.start_node}-${edge.end_node}`;
        setModalKey(lookup);
      }
    },
    [modalKey, allEdges, isIocPage],
  );

  const zoneEdgeLookup = useMemo(() => {
    return Object.values(allEdges).reduce<PartialRecord<string, Edge[]>>(
      (acc, edge) => {
        const edgeZone = edge.zone_id?.toString();
        if (!acc[edgeZone]) acc[edgeZone] = [];
        acc[edgeZone].push(edge);
        return acc;
      },
      {},
    );
  }, [allEdges]);

  const primarySet = useMemo(() => {
    const set = new Set<PathSegment>();
    iocContracts
      .filter((contract) => visibility[contract.id])
      .forEach((contract) => {
        contract.path_node_ids.forEach(([from, to]) => {
          const fromNode = nodes[from];
          const toNode = nodes[to];
          if (fromNode && toNode) set.add([fromNode, toNode]);
        });
      });
    return set;
  }, [iocContracts, visibility, nodes]);

  const secondarySet = useMemo(() => {
    const set = new Set<string>();
    iocContracts
      .filter((contract) => visibility[contract.id])
      .forEach((contract) => {
        contract.receipt_points.forEach((point_id) => {
          const zone = allPoints[point_id]?.receipt_zone?.toString();
          if (!zone) return;
          set.add(zone);
        });
        contract.delivery_points.forEach((point_id) => {
          const zone = allPoints[point_id]?.delivery_zone?.toString();
          if (!zone) return;
          set.add(zone);
        });
      });

    const segments = Array.from(set).flatMap((zone) => {
      const zoneEdges = zoneEdgeLookup[zone];
      if (!zoneEdges) return [];
      return zoneEdges
        .map((edge) => {
          const from = nodes[edge.start_node];
          const to = nodes[edge.end_node];
          return [from, to] as PathSegment;
        })
        .filter(([from, to]) => from && to);
    });
    return segments;
  }, [iocContracts, visibility, allPoints, zoneEdgeLookup, nodes]);

  const primaryFeatures = useMemo(
    () =>
      Array.from(primarySet).map<FeatureLike>(([from, to]) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [from, to].map((node) =>
              fromLonLat([Number(node.lng), Number(node.lat)]),
            ),
          },
          properties: {
            id: `${from.id}-${to.id}`,
            key: `ioc-${from.id}-${to.id}`,
            type: 'edge',
            priority: 0,
          },
        };
      }),
    [primarySet],
  );

  const secondaryFeatures = useMemo(
    () =>
      secondarySet.map<FeatureLike>(([from, to]) => {
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [from, to].map((node) =>
              fromLonLat([Number(node.lng), Number(node.lat)]),
            ),
          },
          properties: {
            id: `${from.id}-${to.id}`,
            key: `ioc-${from.id}-${to.id}`,
            type: 'edge',
            priority: 1,
          },
        };
      }),
    [secondarySet],
  );

  const features = useMemo(() => {
    return primaryFeatures.concat(secondaryFeatures);
  }, [primaryFeatures, secondaryFeatures]);

  const style = useMemo(() => getPathStyle(), []);

  useMapLayer(features, { zIndex, style });

  useEffect(() => {
    registerClickListener(clickListener);
    return () => {
      unregisterClickListener(clickListener);
    };
  }, [clickListener]);

  const modalInfo = useMemo(() => {
    if (!modalKey) return {};

    const [from, to] = modalKey.split('-');
    const fromNode = nodes[from];
    const toNode = nodes[to];
    const receipt_zone =
      allPoints[
        fromNode.points.find((point) => allPoints[point]?.receipt_zone) ?? ''
      ]?.receipt_zone;
    const delivery_zone =
      allPoints[
        toNode.points.find((point) => allPoints[point]?.delivery_zone) ?? ''
      ]?.delivery_zone;
    const zone = receipt_zone ?? delivery_zone;
    const fromPoint = allPoints[fromNode?.points[0]];
    const toPoint = allPoints[toNode?.points[0]];
    const tsp = fromPoint?.tsp_name ?? toPoint?.tsp_name;
    return { fromPoint, toPoint, tsp, zone };
  }, [modalKey, nodes, allPoints]);

  return (
    <IOCSegmentModal
      key={modalKey}
      segmentKey={modalKey}
      onClose={() => setModalKey(null)}
      tspName={modalInfo.tsp}
      fromPoint={modalInfo.fromPoint}
      toPoint={modalInfo.toPoint}
    />
  );
};
